import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { confirmNewsletterSubscription } from '@/api/clients'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { ROUTES } from '@/navigation/constants'
import { localizePath } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'

type ConfirmState = 'loading' | 'confirmed' | 'already_subscribed' | 'invalid' | 'expired' | 'error'

export function NewsletterConfirmPage() {
    const locale = useLandingLocale()
    const [searchParams, setSearchParams] = useSearchParams()
    const [token] = useState(() => searchParams.get('token')?.trim() ?? '')
    const [attempt, setAttempt] = useState(0)
    const [state, setState] = useState<ConfirmState>(() => (token ? 'loading' : 'invalid'))

    useEffect(() => {
        if (!token) return

        const stripTokenFromUrl = () => {
            setSearchParams(
                prev => {
                    if (!prev.has('token')) return prev
                    const next = new URLSearchParams(prev)
                    next.delete('token')
                    return next
                },
                { replace: true },
            )
        }

        let cancelled = false
        setState('loading')
        confirmNewsletterSubscription({ token })
            .then(result => {
                if (cancelled) return
                setState(result.status)
                stripTokenFromUrl()
            })
            .catch(() => {
                if (cancelled) return
                // Keep the token in the URL so refresh / retry can reuse it.
                setState('error')
            })

        return () => {
            cancelled = true
        }
    }, [token, attempt, setSearchParams])

    const title =
        state === 'confirmed'
            ? 'Suscripción confirmada'
            : state === 'already_subscribed'
              ? 'Ya estabas suscrito'
              : state === 'expired'
                ? 'Enlace caducado'
                : state === 'loading'
                  ? 'Confirmando…'
                  : 'No hemos podido confirmar'

    const body =
        state === 'confirmed'
            ? 'Ya formas parte de Novedades Tipi. Te escribimos cada domingo.'
            : state === 'already_subscribed'
              ? 'Tu email ya estaba confirmado. Seguirás recibiendo Novedades Tipi.'
              : state === 'expired'
                ? 'Este enlace ha caducado. Vuelve a suscribirte desde cualquier artículo o página de Tipi.'
                : state === 'loading'
                  ? 'Un momento, estamos confirmando tu email…'
                  : state === 'error'
                    ? 'Ha habido un problema temporal. El enlace sigue siendo válido: pulsa reintentar o vuelve a abrirlo desde el email.'
                    : 'El enlace no es válido. Prueba a suscribirte de nuevo.'

    return (
        <div className='flex min-h-screen flex-col bg-paper font-archivo text-ink antialiased'>
            <LocaleSeoLinks path='/newsletter/confirmar' locale={locale} />
            <PageHelmet locale={locale}>
                <title>{title} | Tipi</title>
                <meta name='robots' content='noindex,nofollow' />
            </PageHelmet>

            <main className='mx-auto flex w-full max-w-[680px] flex-1 flex-col justify-center px-5 py-16'>
                <img
                    src='/newsletter/mascota-jumping.png'
                    alt=''
                    width={72}
                    height={78}
                    className='mb-6 h-[78px] w-[72px] object-contain'
                />
                <p className='m-0 text-xs font-bold uppercase tracking-caps text-flame-press'>Novedades Tipi</p>
                <h1 className='mt-3 text-[32px] font-extrabold leading-9 tracking-display'>{title}</h1>
                <p className='mt-3 text-[17px] leading-[26px] text-ink-2'>{body}</p>
                {state === 'error' ? (
                    <button
                        type='button'
                        onClick={() => setAttempt(current => current + 1)}
                        className='mt-8 inline-flex min-h-[52px] w-fit cursor-pointer items-center justify-center rounded-full border-0 bg-flame-press px-6 text-base font-bold text-white'
                    >
                        Reintentar
                    </button>
                ) : (
                    <Link
                        to={localizePath(ROUTES.home, locale)}
                        className='mt-8 inline-flex min-h-[52px] w-fit items-center justify-center rounded-full bg-flame-press px-6 text-base font-bold text-white'
                    >
                        Volver a Tipi
                    </Link>
                )}
            </main>

            <Footer landingPage='newsletter-confirm' className='border-paper/10 bg-anthracite' />
        </div>
    )
}
